import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import { getFiltersPredicationFromURI } from "components/Filters/helpers";
import { useCachedFilterFields } from "components/Filters/hooks";
import { SortOption } from "components/Filters/types";
import { getSearchQuery } from "components/SearchInput/helpers";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import { FILTER_ITEMS_DICTIONARY, FilterItemKeys } from "./constants";
import { SEARCH_POLICIES_SUGGESTIONS } from "./gql";

const EXCLUDED_FILTER_FIELDS = ["name", FilterItemKeys.PUSH_HASH];
const EXCLUDED_FILTER_VALUES = {
  type: [],
};

const useSearchPoliciesSuggestions = (id: string) => {
  const fieldsRef = useRef<string[] | null>(null);

  const [searchParams] = useSearchParams();
  const searchInput = getSearchQuery(searchParams);

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(searchParams, true);

    return [...(predicatesMap?.values() || [])];
  }, [searchParams]);

  const { data, loading, refetch, previousData } = useQuery(SEARCH_POLICIES_SUGGESTIONS, {
    variables: {
      id,
      input: {
        predicates,
        fullTextSearch: searchInput,
        fields: fieldsRef.current,
      },
    },
  });

  const cachedFiltersData = useCachedFilterFields(
    data?.policy.searchEvaluationRecordSuggestions?.fields
  );
  const sortOptions = useMemo((): SortOption[] => {
    return cachedFiltersData
      .filter((field) => field.orderable)
      .map((field) => {
        return {
          value: field.name,
          label: field.name,
        };
      });
  }, [cachedFiltersData]);

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: FILTER_ITEMS_DICTIONARY,
    excludedFilterFields: EXCLUDED_FILTER_FIELDS,
    excludedFilterValues: EXCLUDED_FILTER_VALUES,
  });

  const handleRefetchActiveSections = useCallback(
    (fields: string[]) => {
      fieldsRef.current = fields.length > 0 ? fields : null;

      if (fields.length > 0) {
        refetch({
          input: {
            fullTextSearch: searchInput,
            predicates,
            fields,
          },
        });
      }
    },
    [refetch, searchInput, predicates]
  );

  const previousSearchRunsSuggestions = previousData?.policy.searchEvaluationRecordSuggestions;

  return {
    filters,
    filtersMap: filterOptionsMap,
    sortOptions,
    loading,
    refetchActiveSections: handleRefetchActiveSections,
    filteredCount:
      data?.policy.searchEvaluationRecordSuggestions?.filteredCount ||
      previousSearchRunsSuggestions?.filteredCount ||
      0,
  };
};

export default useSearchPoliciesSuggestions;
